import type { Color, Config, Content } from '@ml/config';
import { getCell } from '../board/grid.ts';
import { isBoosterLike, isMonochrome, seqTokenOf, type LinkContext } from '../board/link.ts';
import type { Board, Cell, Coord, SeqToken } from '../board/types.ts';

export type Hero = Content['heroes'][number];
export type Skill = Hero['skills'][number];

/** Trạng thái hero cần cho phân loại: hồi chiêu từng slot (0 = dùng được) và thanh ult. */
export type HeroTurnState = {
  readonly cooldowns: readonly number[];
  readonly gauge: number;
};

/**
 * Móc "dãy skill bị thay đổi" (combat.md 4.5). M1 trả nguyên `sequence`; sau này trạng thái
 * như "Hỗn loạn" chỉ sửa hàm này. `slot` 0..2 = skill thường, 3 = ult.
 */
export function getEffectiveSequence(hero: Hero, slot: number): readonly Color[] {
  return slot === 3 ? hero.ultimate.sequence : hero.skills[slot]!.sequence;
}

export function ultReady(state: HeroTurnState, config: Pick<Config, 'combat'>): boolean {
  return state.gauge >= config.combat.ult.gaugeMax;
}

/** Ngữ cảnh lọc tiền tố (orbs.md 3.2) dựng từ hero: skill không hồi chiêu + ult nếu đầy. */
export function linkContextOf(
  hero: Hero,
  state: HeroTurnState,
  config: Pick<Config, 'combat'>,
): LinkContext {
  const patterns: (readonly Color[])[] = [];
  for (let s = 0; s < hero.skills.length; s++) {
    if ((state.cooldowns[s] ?? 0) === 0) patterns.push(getEffectiveSequence(hero, s));
  }
  if (ultReady(state, config)) patterns.push(getEffectiveSequence(hero, 3));
  return { patterns };
}

/** Mẫu khớp `seq`: cùng độ dài, từng vị trí bằng nhau hoặc `*` (orbs.md 3.1). */
export function matchesPattern(seq: readonly SeqToken[], pattern: readonly Color[]): boolean {
  return seq.length === pattern.length && seq.every((s, i) => s === '*' || s === pattern[i]);
}

export type ActionKind =
  'MERGE' | 'BOOSTER_TAP' | 'BOOSTER_COMBO' | 'ULTIMATE' | 'ABILITY' | 'NORMAL' | 'cancel';

export type Classification = {
  readonly kind: ActionKind;
  /** Slot skill (0..2) cho ABILITY, 3 cho ULTIMATE. */
  readonly slot?: number;
  /** Màu NORMAL; `null` = NORMAL không màu (toàn light ball). */
  readonly color?: Color | null;
  readonly seq: readonly SeqToken[];
  /** L = số viên tính vào đòn; S = số ngọc ấn. */
  readonly L: number;
  readonly S: number;
  /**
   * Màu từng viên light ball trong `path` (orbs.md 3.3), theo chỉ số trong path. Light ball của
   * BOOSTER_TAP/COMBO hoặc NORMAL không màu không có mục ở đây (màu chọn lúc kích hoạt).
   */
  readonly lightballColors: ReadonlyMap<number, Color>;
};

function cellsOf(board: Board, path: readonly Coord[]): Cell[] {
  return path.map((c) => {
    const cell = getCell(board, c.x, c.y);
    if (!cell) throw new RangeError(`ô (${c.x},${c.y}) rỗng trong chuỗi`);
    return cell;
  });
}

/**
 * R0 — phân loại chuỗi khi thả (orbs.md 3.2), dừng ở dòng khớp đầu tiên. Không kiểm hình học
 * (resolveRelease gọi validatePath trước). `combat.preferAbilityOverNormal = false` thì NORMAL
 * được xét trước ABILITY (ult vẫn đứng trước).
 */
export function classifyRelease(
  board: Board,
  path: readonly Coord[],
  hero: Hero,
  state: HeroTurnState,
  config: Pick<Config, 'combat' | 'link' | 'essence'>,
): Classification {
  const cells = cellsOf(board, path);
  const seq: SeqToken[] = [];
  const seqIndexOfPath: number[] = [];
  let S = 0;
  cells.forEach((cell, i) => {
    const t = seqTokenOf(cell);
    if (t === null) S++;
    else {
      seqIndexOfPath[i] = seq.length;
      seq.push(t);
    }
  });
  const L = seq.length;
  const none = new Map<number, Color>();
  const base = { seq, L, S, lightballColors: none };
  const colorize = (colorAt: (seqIndex: number) => Color): Map<number, Color> => {
    const m = new Map<number, Color>();
    cells.forEach((cell, i) => {
      if (cell.t === 'lightball') m.set(i, colorAt(seqIndexOfPath[i]!));
    });
    return m;
  };

  if (L === 0) {
    return { ...base, kind: S >= config.essence.mergeMinCount ? 'MERGE' : 'cancel' };
  }
  if (cells.length === 1 && isBoosterLike(cells[0]!)) return { ...base, kind: 'BOOSTER_TAP' };
  if (cells.length === 2 && cells.every(isBoosterLike)) return { ...base, kind: 'BOOSTER_COMBO' };

  const ult = getEffectiveSequence(hero, 3);
  if (ultReady(state, config) && matchesPattern(seq, ult)) {
    return { ...base, kind: 'ULTIMATE', slot: 3, lightballColors: colorize((k) => ult[k]!) };
  }

  const normalColor = (): Color | null | undefined => {
    if (L < config.link.minLength || !isMonochrome(seq)) return undefined;
    return (seq.find((s) => s !== '*') as Color | undefined) ?? null;
  };
  const asNormal = (): Classification | undefined => {
    const c = normalColor();
    if (c === undefined) return undefined;
    return {
      ...base,
      kind: 'NORMAL',
      color: c,
      lightballColors: c === null ? none : colorize(() => c),
    };
  };

  if (!config.combat.preferAbilityOverNormal) {
    const n = asNormal();
    if (n) return n;
  }
  for (let s = 0; s < hero.skills.length; s++) {
    if ((state.cooldowns[s] ?? 0) !== 0) continue;
    const pat = getEffectiveSequence(hero, s);
    if (matchesPattern(seq, pat)) {
      return { ...base, kind: 'ABILITY', slot: s, lightballColors: colorize((k) => pat[k]!) };
    }
  }
  return asNormal() ?? { ...base, kind: 'cancel' };
}
